import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, Flame, Droplets, Wallet, Snowflake } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JsonLd from "@/components/JsonLd";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SITE_URL, scrollToId } from "@/lib/api";

const MATERIALS = [
  {
    key: "eps", name: "EPS 70F (biely polystyrén)", lambda: "0,038 – 0,040",
    fire: "Trieda E (horľavý)", vapor: "Nízka – stredná", price: "€ (najnižšia)",
    use: "Rodinné domy — najlepší pomer cena/výkon.",
    pros: ["Najdostupnejšia cena", "Ľahká montáž", "Overený štandard pre RD"],
    cons: ["Horľavý (trieda E)", "Nižšia paropriepustnosť"],
  },
  {
    key: "eps_grafit", name: "Grafitový EPS (šedý)", lambda: "0,031 – 0,033",
    fire: "Trieda E (horľavý)", vapor: "Nízka – stredná", price: "€€ (stredná)",
    use: "Kde treba vyšší tepelný odpor pri menšej hrúbke.",
    pros: ["Lepší izolant pri menšej hrúbke", "Úspora priestoru na ostení", "Nižšia spotreba materiálu"],
    cons: ["Drahší ako biely EPS", "Pri montáži citlivý na priame slnko"],
  },
  {
    key: "mineral", name: "Minerálna vata (fasádna)", lambda: "0,036 – 0,041",
    fire: "Trieda A1 (nehorľavá)", vapor: "Vysoká", price: "€€€ (vyššia)",
    use: "Bytové domy, požiarne pásy, difúzne otvorené konštrukcie.",
    pros: ["Nehorľavá (A1)", "Vysoká paropriepustnosť", "Dobrá zvuková izolácia"],
    cons: ["Vyššia cena", "Vyššia hmotnosť", "Náročnejšia montáž"],
  },
  {
    key: "xps", name: "XPS (extrudovaný polystyrén)", lambda: "0,030 – 0,035",
    fire: "Trieda E (horľavý)", vapor: "Veľmi nízka", price: "€€€ (vyššia)",
    use: "Sokel a spodná stavba — kontakt so zemou a vlhkosťou.",
    pros: ["Vysoká pevnosť v tlaku", "Odolný voči vlhkosti", "Ideálny na sokel"],
    cons: ["Nevhodný na celú fasádu", "Vyššia cena", "Veľmi nízka paropriepustnosť"],
  },
];

const ROWS = [
  { label: "Tepelná vodivosť λ (W/m·K)", key: "lambda", icon: Snowflake, hint: "nižšie = lepší izolant" },
  { label: "Horľavosť", key: "fire", icon: Flame },
  { label: "Paropriepustnosť", key: "vapor", icon: Droplets },
  { label: "Cena", key: "price", icon: Wallet },
  { label: "Typické použitie", key: "use" },
];

const FAQ = [
  { q: "EPS alebo minerálna vata — čo je lepšie?", a: "EPS (polystyrén) je lacnejší a má výborný pomer cena/výkon — je najčastejšou voľbou pre rodinné domy. Minerálna vata je nehorľavá (trieda A1) a paropriepustná, preto sa používa na bytové domy, požiarne pásy a difúzne otvorené konštrukcie. Ak je hlavným kritériom cena, volí sa EPS; ak požiarna bezpečnosť a paropriepustnosť, minerálna vata." },
  { q: "Aký je rozdiel medzi bielym a grafitovým polystyrénom?", a: "Grafitový (šedý) EPS má nižšiu tepelnú vodivosť (λ ≈ 0,031) než biely EPS (λ ≈ 0,038), takže pri rovnakej hrúbke lepšie izoluje — alebo dosiahnete rovnaký tepelný odpor pri menšej hrúbke. Je o niečo drahší, ale šetrí priestor na ostení okien." },
  { q: "Kde sa používa XPS?", a: "XPS (extrudovaný polystyrén) má vysokú pevnosť a odolnosť voči vlhkosti, preto sa používa najmä na sokel a spodnú stavbu, ktorá je v kontakte so zemou a vodou. Na celú fasádu sa bežne nepoužíva." },
  { q: "Ktorý materiál je najlacnejší?", a: "Najnižšiu cenu má biely fasádny EPS 70F. Grafitový EPS je o niečo drahší, minerálna vata a XPS patria k drahším riešeniam. Presnú cenu pre váš dom vypočíta naša kalkulačka." },
];

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

export default function MaterialCompare() {
  useEffect(() => {
    document.title = "Porovnanie materiálov na zateplenie: EPS, grafit, vata, XPS — Zateplenia.eu";
    setMeta("description", "EPS vs. grafitový polystyrén vs. minerálna vata vs. XPS — porovnanie tepelnej vodivosti, ceny, horľavosti a použitia. Poradíme, ktorý materiál zvoliť na zateplenie.");
    window.scrollTo(0, 0);
  }, []);

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/materialy#faq`,
    "mainEntity": FAQ.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <JsonLd id="ld-materials" data={faqLd} />
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="materials-page">
        <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Porovnanie materiálov</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] text-white max-w-4xl">
          Aký materiál na <span className="text-gold">zateplenie</span> zvoliť?
        </h1>
        <p className="text-white/55 text-sm md:text-base mt-5 max-w-2xl">
          Prehľadné porovnanie najčastejších materiálov na zateplenie fasády — polystyrén (EPS),
          grafitový EPS, minerálna vata a XPS. Pozrite si tepelnú vodivosť, cenu, horľavosť aj vhodné použitie.
        </p>

        {/* Comparison table */}
        <div className="mt-12 overflow-x-auto border border-white/10" data-testid="materials-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-ink-2">
                <th className="text-left px-5 py-4 font-mono2 text-xs uppercase tracking-widest text-white/40 w-52">Vlastnosť</th>
                {MATERIALS.map((m) => (
                  <th key={m.key} className="text-left px-5 py-4 text-white font-semibold border-l border-white/10">{m.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.key} className="border-t border-white/8">
                  <td className="px-5 py-4 text-white/60 align-top">
                    <span className="flex items-center gap-2">{row.icon && <row.icon className="w-4 h-4 text-gold" />} {row.label}</span>
                    {row.hint && <span className="block text-white/30 text-[11px] mt-1">{row.hint}</span>}
                  </td>
                  {MATERIALS.map((m) => (
                    <td key={m.key} className="px-5 py-4 text-white/80 border-l border-white/10 align-top">{m[row.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pros / cons cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-10">
          {MATERIALS.map((m) => (
            <div key={m.key} className="border border-white/10 bg-ink-2 p-6" data-testid={`material-card-${m.key}`}>
              <h3 className="text-white font-semibold text-base leading-snug mb-1">{m.name}</h3>
              <p className="text-gold/80 font-mono2 text-xs mb-4">λ {m.lambda}</p>
              <ul className="space-y-2 mb-4">
                {m.pros.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-white/70 text-sm"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
              <ul className="space-y-2">
                {m.cons.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-white/50 text-sm"><X className="w-4 h-4 text-red-400/70 shrink-0 mt-0.5" /> {c}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <h2 className="font-display text-3xl md:text-4xl text-white mt-16 mb-6">Časté otázky o materiáloch</h2>
        <Accordion type="single" collapsible className="max-w-3xl" data-testid="materials-faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`m-${i}`} className="border-white/10" data-testid={`materials-faq-${i}`}>
              <AccordionTrigger className="text-white font-semibold text-base md:text-lg text-left hover:no-underline hover:text-gold [&[data-state=open]]:text-gold py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-white/60 text-sm md:text-base leading-relaxed pr-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Related articles */}
        <div className="mt-14">
          <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Súvisiace články</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/blog/spotreba-materialov-pri-zateplovani" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Spotreba materiálov pri zatepľovaní</Link>
            <Link to="/blog/pozor-na-rosny-bod-pri-zatepleni" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Pozor na rosný bod pri zateplení</Link>
            <Link to="/blog/5-rad-pri-zateplovani" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">5 rád pri zatepľovaní</Link>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 border border-gold/30 bg-gold/5 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-testid="materials-cta">
          <div>
            <h2 className="font-display text-2xl md:text-3xl text-white">Vypočítajte cenu s vaším materiálom</h2>
            <p className="text-white/55 text-sm mt-2">Kalkulačka počíta cenu pre EPS, grafit, vatu aj XPS.</p>
          </div>
          <Link to="/#kalkulacka" onClick={() => setTimeout(() => scrollToId("#kalkulacka"), 60)} data-testid="materials-cta-btn"
            className="inline-flex items-center justify-center gap-2 bg-gold text-ink px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors whitespace-nowrap">
            Vypočítať cenu <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
